/**
 * Выбор узла KLS для типа заявки РД (qual_id = 320) с поиском по наименованию и коду.
 * Пустое значение — шаблон без привязки к конкретному типу («все типы»), как в метаданных шаблона РД.
 */

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { klsSectionsApi } from "@/services/kls-api.service";
import type { KlsSearchResult, KlsWithQual } from "@/types/api-v2";
import {
  klsHierarchyRowPaddingLeftPx,
  klsRubrikaIndentLevel,
  sortKlsComboboxRowsByRubrika,
  toKlsComboboxRows,
} from "@/utils/klsRubrikaHierarchy";

/** Квалификатор типов заявок РД (согласовано с backend `QUAL_ID_RD_REQUEST_TYPES`). */
export const RD_REQUEST_TYPE_QUAL_ID = 320;

function formatKlsPrimary(k: {
  kls_namef: string;
  kls_names?: string | null;
}): string {
  const s = k.kls_names?.trim();
  return s || k.kls_namef || "—";
}

function formatKlsSubtitle(k: { kls_code: string; kls_id: number }): string {
  return `${k.kls_code} · id ${k.kls_id}`;
}

export interface RdRequestTypeKlsComboboxProps {
  value: number | "";
  onValueChange: (klsId: number | "") => void;
  disabled?: boolean;
  className?: string;
}

export function RdRequestTypeKlsCombobox({
  value,
  onValueChange,
  disabled = false,
  className,
}: RdRequestTypeKlsComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), 280);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (open) {
      setSearchInput("");
      setDebouncedSearch("");
    }
  }, [open]);

  const { data: selectedKls } = useQuery({
    queryKey: ["kls", "by-id", value],
    queryFn: () => klsSectionsApi.get(value as number),
    enabled: typeof value === "number" && value > 0,
    staleTime: 60_000,
  });

  const searching = debouncedSearch.trim().length > 0;

  const { data: preview = [], isLoading: previewLoading } = useQuery({
    queryKey: ["kls", "rd-request-types-preview", RD_REQUEST_TYPE_QUAL_ID],
    queryFn: () =>
      klsSectionsApi.list({
        qual_id: RD_REQUEST_TYPE_QUAL_ID,
        limit: 3000,
        include_deleted: false,
      }),
    enabled: open && !searching,
    staleTime: 60_000,
  });

  const { data: searchHits = [], isFetching: searchFetching } = useQuery({
    queryKey: [
      "kls",
      "rd-request-types-search",
      RD_REQUEST_TYPE_QUAL_ID,
      debouncedSearch.trim(),
    ],
    queryFn: () =>
      klsSectionsApi.search({
        q: debouncedSearch.trim(),
        qual_id: RD_REQUEST_TYPE_QUAL_ID,
        limit: 50,
      }),
    enabled: open && searching,
    staleTime: 0,
  });

  const rows = useMemo(() => {
    const raw: (KlsWithQual | KlsSearchResult)[] = searching ? searchHits : preview;
    return sortKlsComboboxRowsByRubrika(toKlsComboboxRows(raw));
  }, [searching, searchHits, preview]);

  const loading = searching ? searchFetching : previewLoading;

  const valueStr = value === "" ? "" : String(value);
  const triggerLabel = useMemo(() => {
    if (value === "" || (typeof value === "number" && value <= 0)) {
      return "Не задано — все типы заявок";
    }
    if (selectedKls) {
      return `${formatKlsPrimary(selectedKls)} (${formatKlsSubtitle(selectedKls)})`;
    }
    return `KLS id ${value}`;
  }, [value, selectedKls]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
          title={triggerLabel}
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0 z-[9999]"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Поиск по названию или коду…"
            value={searchInput}
            onValueChange={setSearchInput}
          />
          <CommandList className="max-h-[280px]">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Загрузка…
              </div>
            ) : (
              <>
                <CommandGroup heading="Привязка">
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onValueChange("");
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        valueStr === "" ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span>Не задано — все типы заявок</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
                <CommandEmpty>
                  {searching
                    ? "Ничего не найдено — уточните запрос"
                    : "Нет записей типов заявок РД (qual 320)"}
                </CommandEmpty>
                <CommandGroup>
                  {rows.map((k) => {
                    const idStr = String(k.kls_id);
                    const primary = formatKlsPrimary(k);
                    const sub = formatKlsSubtitle(k);
                    const indent = klsRubrikaIndentLevel(k.kls_rubrika);
                    const padLeft = klsHierarchyRowPaddingLeftPx(indent);
                    return (
                      <CommandItem
                        key={k.kls_id}
                        value={idStr}
                        onSelect={() => {
                          onValueChange(Number(idStr));
                          setOpen(false);
                        }}
                        title={`${primary} — ${sub}`}
                        className="cursor-pointer"
                        style={{ paddingLeft: padLeft }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 shrink-0",
                            valueStr === idStr ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">
                            {indent > 0 ? (
                              <span className="text-muted-foreground mr-1 select-none">
                                └
                              </span>
                            ) : null}
                            {primary}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {sub}
                          </div>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
